/**
 * FILE: packages\supabase\src\services\demoData.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */

/**
 * FILE: packages/supabase/src/services/demoData.ts
 * PURPOSE: Complete simulated mock data for CEO demo — phone 9404626275 only.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 */

export const DEMO_PHONE = "9999999999"

export function isDemoPhone(phone: string | null | undefined): boolean {
  return phone?.replace(/\D/g, "").slice(-10) === DEMO_PHONE
}

/* ─── Learn Data ─── */

export const demoLearnData = {
  last_synced_at: new Date().toISOString(),
  syllabus: [
    { topic_code: "ethics_1", topic_name: "Ethics & Trust", subject: "Ethics", moodle_url: null, order_index: 1, progress_percent: 100, moodle_module_id: null },
    { topic_code: "ethics_2", topic_name: "Code of Standards", subject: "Ethics", moodle_url: null, order_index: 2, progress_percent: 100, moodle_module_id: null },
    { topic_code: "ethics_3", topic_name: "GIPS", subject: "Ethics", moodle_url: null, order_index: 3, progress_percent: 85, moodle_module_id: null },
    { topic_code: "quant_1", topic_name: "Time Value of Money", subject: "Quant", moodle_url: null, order_index: 4, progress_percent: 100, moodle_module_id: null },
    { topic_code: "quant_2", topic_name: "Discounted Cash Flow", subject: "Quant", moodle_url: null, order_index: 5, progress_percent: 100, moodle_module_id: null },
    { topic_code: "quant_3", topic_name: "Hypothesis Testing", subject: "Quant", moodle_url: null, order_index: 6, progress_percent: 72, moodle_module_id: null },
    { topic_code: "fra_1", topic_name: "Financial Statements", subject: "FRA", moodle_url: null, order_index: 7, progress_percent: 100, moodle_module_id: null },
    { topic_code: "fra_2", topic_name: "Ratios & Analysis", subject: "FRA", moodle_url: null, order_index: 8, progress_percent: 90, moodle_module_id: null },
    { topic_code: "fra_3", topic_name: "Inventories", subject: "FRA", moodle_url: null, order_index: 9, progress_percent: 65, moodle_module_id: null },
    { topic_code: "fra_4", topic_name: "Long-Lived Assets", subject: "FRA", moodle_url: null, order_index: 10, progress_percent: 40, moodle_module_id: null },
    { topic_code: "corp_1", topic_name: "Capital Budgeting", subject: "Corporate Finance", moodle_url: null, order_index: 11, progress_percent: 100, moodle_module_id: null },
    { topic_code: "corp_2", topic_name: "Cost of Capital", subject: "Corporate Finance", moodle_url: null, order_index: 12, progress_percent: 80, moodle_module_id: null },
    { topic_code: "equity_1", topic_name: "Market Organisation", subject: "Equity", moodle_url: null, order_index: 13, progress_percent: 100, moodle_module_id: null },
    { topic_code: "equity_2", topic_name: "Industry Analysis", subject: "Equity", moodle_url: null, order_index: 14, progress_percent: 55, moodle_module_id: null },
    { topic_code: "equity_3", topic_name: "Valuation", subject: "Equity", moodle_url: null, order_index: 15, progress_percent: 20, moodle_module_id: null },
    { topic_code: "fi_1", topic_name: "Fixed Income Features", subject: "Fixed Income", moodle_url: null, order_index: 16, progress_percent: 100, moodle_module_id: null },
    { topic_code: "fi_2", topic_name: "Yield Measures", subject: "Fixed Income", moodle_url: null, order_index: 17, progress_percent: 60, moodle_module_id: null },
    { topic_code: "deriv_1", topic_name: "Derivatives Basics", subject: "Derivatives", moodle_url: null, order_index: 18, progress_percent: 45, moodle_module_id: null },
    { topic_code: "deriv_2", topic_name: "Option Pricing", subject: "Derivatives", moodle_url: null, order_index: 19, progress_percent: 10, moodle_module_id: null },
    { topic_code: "alt_1", topic_name: "Alternative Investments", subject: "Alternative", moodle_url: null, order_index: 20, progress_percent: 0, moodle_module_id: null },
    { topic_code: "pm_1", topic_name: "Portfolio Risk & Return", subject: "Portfolio", moodle_url: null, order_index: 21, progress_percent: 30, moodle_module_id: null },
    { topic_code: "pm_2", topic_name: "Modern Portfolio Theory", subject: "Portfolio", moodle_url: null, order_index: 22, progress_percent: 0, moodle_module_id: null },
  ],
  quizzes: [
    { id: "q1", moodle_item_id: 101, attempt_number: 1, score_percent: 82, completed_at: "2026-04-28T10:00:00Z", fincoin_award: 10 },
    { id: "q2", moodle_item_id: 102, attempt_number: 1, score_percent: 91, completed_at: "2026-04-30T11:30:00Z", fincoin_award: 15 },
    { id: "q3", moodle_item_id: 103, attempt_number: 1, score_percent: 76, completed_at: "2026-05-02T09:15:00Z", fincoin_award: 10 },
    { id: "q4", moodle_item_id: 104, attempt_number: 1, score_percent: 88, completed_at: "2026-05-05T14:00:00Z", fincoin_award: 12 },
  ],
  mocks: [
    { id: "m1", moodle_item_id: 201, attempt_number: 1, score_percent: 68, completed_at: "2026-04-15T10:00:00Z", passed: true, is_best: false },
    { id: "m2", moodle_item_id: 201, attempt_number: 2, score_percent: 74, completed_at: "2026-04-22T11:00:00Z", passed: true, is_best: true },
    { id: "m3", moodle_item_id: 202, attempt_number: 1, score_percent: 71, completed_at: "2026-05-01T09:00:00Z", passed: true, is_best: true },
  ],
  next_up: {
    topic_code: "fra_4",
    topic_name: "Long-Lived Assets",
    subject: "FRA",
    moodle_url: null,
    progress_percent: 40,
    estimated_minutes: 45,
  },
}

/* ─── Schedule ─── */

const today = new Date().toISOString().slice(0, 10)
const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10)
const dayAfter = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10)

export const demoSchedule = [
  {
    id: "sch-1",
    course: "CFA_L1",
    attempt: "2026_Jan",
    session_date: today,
    day_of_week: "Today",
    session_time: "6:30 PM",
    subject: "Equity",
    session_topics: "Valuation Models — DDM & multiples",
    prerequisite_videos: "Equity Intro, DCF Basics",
    weekly_test_code: "EQ_W05",
    weekly_test_released_date: today,
    weekly_test_deadline: tomorrow,
    program: "LiveSchool",
    zoom_link: "https://zoom.com/join",
    status: "scheduled",
    title: "CFA L1 — Equity Valuation Live",
    scheduled_at: `${today}T18:30:00`,
  },
  {
    id: "sch-2",
    course: "CFA_L1",
    attempt: "2026_Jan",
    session_date: tomorrow,
    day_of_week: "Tomorrow",
    session_time: "6:30 PM",
    subject: "Fixed Income",
    session_topics: "Yield Measures & Spread Analysis",
    prerequisite_videos: "Fixed Income Basics",
    weekly_test_code: "FI_W06",
    weekly_test_released_date: tomorrow,
    weekly_test_deadline: dayAfter,
    program: "LiveSchool",
    zoom_link: "https://zoom.com/join",
    status: "scheduled",
    title: "CFA L1 — Fixed Income Live",
    scheduled_at: `${tomorrow}T18:30:00`,
  },
  {
    id: "sch-3",
    course: "CFA_L1",
    attempt: "2026_Jan",
    session_date: dayAfter,
    day_of_week: "Day after",
    session_time: "6:30 PM",
    subject: "Derivatives",
    session_topics: "Options Basics & Payoff Diagrams",
    prerequisite_videos: "Derivatives Intro",
    weekly_test_code: "DER_W07",
    weekly_test_released_date: dayAfter,
    weekly_test_deadline: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
    program: "LiveSchool",
    zoom_link: "https://zoom.com/join",
    status: "scheduled",
    title: "CFA L1 — Derivatives Live",
    scheduled_at: `${dayAfter}T18:30:00`,
  },
]

export const demoUpcomingSessions = [demoSchedule[0], demoSchedule[1]]

export const demoVerifyLiveClass = {
  allowed: true,
  zoomLink: "https://zoom.com/join",
  title: "CFA L1 — Equity Valuation Live",
  scheduledAt: `${today}T18:30:00`,
}

/* ─── Test Streak & Leaderboard ─── */

export const demoTestStreak = {
  currentStreak: 12,
  longestStreak: 18,
  totalTests: 24,
  avgScore: 78,
}

/* ─── Profile ─── */

export const demoProfile = {
  phone: DEMO_PHONE,
  name: "Shreyash",
  avatar_url: null,
  course: "CFA_L1",
  batch: "Jan 2026",
  graduation_year: 2026,
  company: "Student",
  role: "Finance Intern",
  city: "Pune",
  linkedin_url: "https://linkedin.com/in/shreyash-demo",
  exp_band: "0-1",
  qualification: "CFA_L1_STUDENT" as const,
  completion_percent: 85,
}

export const demoStudentProfileOverview = {
  student: {
    phone: DEMO_PHONE,
    name: "Shreyash",
    email: "shreyash@fintree.edu",
    course: "CFA_L1",
    city: "Pune",
    program: "LiveSchool",
  },
  candidate: {
    phone: DEMO_PHONE,
    current_position: "Finance Intern",
    experience_years: 1,
    education: "B.Com, Symbiosis Pune",
    skills: "Financial Analysis, Excel, Python",
    resume_url: null,
    linkedin_url: "https://linkedin.com/in/shreyash-demo",
  },
  enrollments: [
    { id: "e1", course: "CFA Level I", attempt: "Jan 2026", status: "Active" },
    { id: "e2", course: "Financial Modeling", attempt: "2025", status: "Completed" },
  ],
  applications: [],
}

/* ─── FinCoins ─── */

export const demoFinCoinBalance = {
  phone: DEMO_PHONE,
  balance: 1250,
  lifetime_earned: 2100,
  lifetime_redeemed: 850,
}

export const demoFinCoinTransactions = [
  { id: "tx1", phone: DEMO_PHONE, amount: 15, action: "earn" as const, description: "Quiz score 91%", created_at: "2026-05-05T14:00:00Z" },
  { id: "tx2", phone: DEMO_PHONE, amount: 10, action: "earn" as const, description: "Daily streak bonus", created_at: "2026-05-05T06:00:00Z" },
  { id: "tx3", phone: DEMO_PHONE, amount: 5, action: "earn" as const, description: "YouTube playlist opened", created_at: "2026-05-04T19:30:00Z" },
  { id: "tx4", phone: DEMO_PHONE, amount: 20, action: "earn" as const, description: "Mock exam passed 74%", created_at: "2026-05-02T09:00:00Z" },
  { id: "tx5", phone: DEMO_PHONE, amount: -50, action: "spend" as const, description: "Connection request (cross-batch)", created_at: "2026-05-01T12:00:00Z" },
  { id: "tx6", phone: DEMO_PHONE, amount: 12, action: "earn" as const, description: "Weekly test submitted", created_at: "2026-04-30T11:30:00Z" },
  { id: "tx7", phone: DEMO_PHONE, amount: 10, action: "earn" as const, description: "Syllabus topic completed", created_at: "2026-04-28T10:00:00Z" },
  { id: "tx8", phone: DEMO_PHONE, amount: -100, action: "redeem" as const, description: "Streak freeze purchased", created_at: "2026-04-25T08:00:00Z" },
]

/* ─── XP / Levels ─── */

export const demoXPProgress = {
  current_level: 5,
  level_name: "Analyst",
  current_xp: 2450,
  next_level_xp: 3000,
  progress_percent: 82,
  daily_xp_today: 45,
  daily_xp_remaining: 455,
  current_level_reward_description: "Unlock priority doubt clearing",
}

/* ─── Badges ─── */

export const demoBadges = [
  { id: "b1", student_phone: DEMO_PHONE, badge_id: "streak_7", earned_at: "2026-04-10T00:00:00Z", created_at: "2026-04-10T00:00:00Z" },
  { id: "b2", student_phone: DEMO_PHONE, badge_id: "streak_30", earned_at: "2026-04-25T00:00:00Z", created_at: "2026-04-25T00:00:00Z" },
  { id: "b3", student_phone: DEMO_PHONE, badge_id: "mock_70", earned_at: "2026-04-22T00:00:00Z", created_at: "2026-04-22T00:00:00Z" },
  { id: "b4", student_phone: DEMO_PHONE, badge_id: "syllabus_50", earned_at: "2026-04-18T00:00:00Z", created_at: "2026-04-18T00:00:00Z" },
  { id: "b5", student_phone: DEMO_PHONE, badge_id: "quiz_perfect", earned_at: "2026-04-30T00:00:00Z", created_at: "2026-04-30T00:00:00Z" },
  { id: "b6", student_phone: DEMO_PHONE, badge_id: "practice_starter", earned_at: "2026-04-05T00:00:00Z", created_at: "2026-04-05T00:00:00Z" },
  { id: "b7", student_phone: DEMO_PHONE, badge_id: "complete_first_topic", earned_at: "2026-04-02T00:00:00Z", created_at: "2026-04-02T00:00:00Z" },
  { id: "b8", student_phone: DEMO_PHONE, badge_id: "use_all_tools", earned_at: "2026-05-03T00:00:00Z", created_at: "2026-05-03T00:00:00Z" },
]

/* ─── Missions & Quests ─── */

export const demoMissions = [
  { id: "m1", phone: DEMO_PHONE, mission_id: "weekly_test", progress: 1, target: 1, completed_at: "2026-05-05T11:00:00Z", expires_at: "2026-05-11T23:59:59Z", created_at: "2026-05-05T00:00:00Z" },
  { id: "m2", phone: DEMO_PHONE, mission_id: "watch_3_videos", progress: 2, target: 3, completed_at: null, expires_at: "2026-05-11T23:59:59Z", created_at: "2026-05-05T00:00:00Z" },
  { id: "m3", phone: DEMO_PHONE, mission_id: "complete_2_topics", progress: 1, target: 2, completed_at: null, expires_at: "2026-05-11T23:59:59Z", created_at: "2026-05-05T00:00:00Z" },
  { id: "m4", phone: DEMO_PHONE, mission_id: "daily_login", progress: 5, target: 5, completed_at: "2026-05-05T06:00:00Z", expires_at: "2026-05-06T23:59:59Z", created_at: "2026-05-05T00:00:00Z" },
]

export const demoDailyQuests = [
  { id: "dq1", phone: DEMO_PHONE, quest_id: "morning_quiz", progress: 1, target: 1, completed_at: "2026-05-08T08:00:00Z", expires_at: "2026-05-09T06:00:00Z", created_at: "2026-05-08T00:00:00Z" },
  { id: "dq2", phone: DEMO_PHONE, quest_id: "pomodoro_25", progress: 1, target: 2, completed_at: null, expires_at: "2026-05-09T06:00:00Z", created_at: "2026-05-08T00:00:00Z" },
  { id: "dq3", phone: DEMO_PHONE, quest_id: "share_card", progress: 0, target: 1, completed_at: null, expires_at: "2026-05-09T06:00:00Z", created_at: "2026-05-08T00:00:00Z" },
]

/* ─── Study Streak ─── */

export const demoStudyStreak = {
  phone: DEMO_PHONE,
  streak_days: 12,
  last_study_date: today,
  longest_streak: 18,
  streak_freeze_balance: 2,
  streak_freeze_pending: false,
  streak_recovery_mode: false,
  streak_recovery_window_start: null,
  streak_recovery_topics: 0,
  freeze_monthly_already_used: false,
}

/* ─── YouTube Playlists ─── */

export const demoYouTubePlaylists = [
  {
    id: "yt-1",
    youtube_playlist_id: "PL_alt_inv",
    title: "CFA L1 — Alternative Investments Live",
    description: "Complete live class on Alternative Investments for CFA Level I.",
    course: "CFA_L1",
    category: "Alternative",
    video_count: 1,
    thumbnail_url: "https://img.youtube.com/vi/5x12p9Fc8C0/mqdefault.jpg",
  },
  {
    id: "yt-2",
    youtube_playlist_id: "PL_black_scholes",
    title: "CFA L1 — Black-Scholes Models",
    description: "Deep dive into Black-Scholes option pricing models.",
    course: "CFA_L1",
    category: "Derivatives",
    video_count: 1,
    thumbnail_url: "https://img.youtube.com/vi/PiqS5HuSWUE/mqdefault.jpg",
  },
  {
    id: "yt-3",
    youtube_playlist_id: "PL_fixed_income",
    title: "CFA L1 — Fixed Income",
    description: "Fixed Income concepts and valuation for CFA Level I.",
    course: "CFA_L1",
    category: "Fixed Income",
    video_count: 1,
    thumbnail_url: "https://img.youtube.com/vi/gKCWPN-32kY/mqdefault.jpg",
  },
  {
    id: "yt-4",
    youtube_playlist_id: "PL_l2_crash",
    title: "CFA Level 2 — Crash Course",
    description: "Intensive crash course for CFA Level 2 aspirants.",
    course: "CFA_L2",
    category: "Crash Course",
    video_count: 1,
    thumbnail_url: "https://img.youtube.com/vi/CmgJHnwjIwc/mqdefault.jpg",
  },
]

/* ─── Reels ─── */

export const demoReels = [
  {
    id: "r1",
    media_url: "https://www.youtube.com/shorts/5x12p9Fc8C0",
    thumbnail_url: "https://img.youtube.com/vi/5x12p9Fc8C0/mqdefault.jpg",
    caption: "Alternative Investments explained in 60 seconds",
    category: "Exam Strategy",
    course: "CFA_L1",
    topic: "Alternative Investments",
    created_at: "2026-05-01T10:00:00Z",
  },
  {
    id: "r2",
    media_url: "https://www.youtube.com/shorts/PiqS5HuSWUE",
    thumbnail_url: "https://img.youtube.com/vi/PiqS5HuSWUE/mqdefault.jpg",
    caption: "Black-Scholes simplified for CFA",
    category: "Derivatives",
    course: "CFA_L1",
    topic: "Option Pricing",
    created_at: "2026-05-02T10:00:00Z",
  },
  {
    id: "r3",
    media_url: "https://www.youtube.com/shorts/gKCWPN-32kY",
    thumbnail_url: "https://img.youtube.com/vi/gKCWPN-32kY/mqdefault.jpg",
    caption: "Fixed Income yield measures quick recap",
    category: "Fixed Income",
    course: "CFA_L1",
    topic: "Yield Measures",
    created_at: "2026-05-03T10:00:00Z",
  },
  {
    id: "r4",
    media_url: "https://www.youtube.com/shorts/CmgJHnwjIwc",
    thumbnail_url: "https://img.youtube.com/vi/CmgJHnwjIwc/mqdefault.jpg",
    caption: "L2 Crash Course — what to focus on",
    category: "Exam Strategy",
    course: "CFA_L2",
    topic: "Study Plan",
    created_at: "2026-05-04T10:00:00Z",
  },
  {
    id: "r5",
    media_url: "https://www.instagram.com/reel/demo1",
    thumbnail_url: "https://img.youtube.com/vi/5x12p9Fc8C0/mqdefault.jpg",
    caption: "Ethics decision tree — CFA tip",
    category: "Ethics",
    course: "CFA_L1",
    topic: "Code of Standards",
    created_at: "2026-05-05T10:00:00Z",
  },
  {
    id: "r6",
    media_url: "https://www.instagram.com/reel/demo2",
    thumbnail_url: "https://img.youtube.com/vi/PiqS5HuSWUE/mqdefault.jpg",
    caption: "Quant formulas you must memorise",
    category: "Quant",
    course: "CFA_L1",
    topic: "Formulas",
    created_at: "2026-05-06T10:00:00Z",
  },
]

export const demoSavedReels = [demoReels[0], demoReels[2]]

/* ─── Jobs ─── */

const now = new Date()
const d3 = new Date(now.getTime() + 3 * 86400000).toISOString()
const d7 = new Date(now.getTime() + 7 * 86400000).toISOString()
const d14 = new Date(now.getTime() + 14 * 86400000).toISOString()
const d1 = new Date(now.getTime() + 86400000).toISOString()
const d10 = new Date(now.getTime() + 10 * 86400000).toISOString()
const d5 = new Date(now.getTime() + 5 * 86400000).toISOString()

export const demoJobs = [
  {
    id: "j1",
    title: "Equity Research Analyst",
    company: null,
    location: "Mumbai",
    job_type: "Full-time",
    description: "Research coverage of BFSI sector. CFA L1/L2 preferred.",
    course_required: "CFA_L1,CFA_L2",
    exp_band_required: "0-1",
    skills_required: "Excel, Financial Modeling",
    posted_date: "2026-05-01T00:00:00Z",
    deadline: d3,
    status: "open",
    core_qualifications: ["CFA_L1"],
    min_cleared_level: "CFA_L1",
  },
  {
    id: "j2",
    title: "Risk Analyst",
    company: "ICICI Bank",
    location: "Mumbai",
    job_type: "Full-time",
    description: "Credit risk modelling and portfolio analytics.",
    course_required: "CFA_L1,CFA_L2,FRM_P1",
    exp_band_required: "1-3",
    skills_required: "Python, SQL, Risk Models",
    posted_date: "2026-04-28T00:00:00Z",
    deadline: d7,
    status: "open",
    core_qualifications: ["CFA_L1", "FRM_P1"],
    min_cleared_level: "CFA_L1",
  },
  {
    id: "j3",
    title: "Investment Banking Associate",
    company: "Axis Capital",
    location: "Mumbai",
    job_type: "Full-time",
    description: "M&A advisory and capital markets execution.",
    course_required: "CFA_L2,CFA_L3",
    exp_band_required: "3-5",
    skills_required: "Valuation, DCF, Pitch Books",
    posted_date: "2026-04-25T00:00:00Z",
    deadline: d14,
    status: "open",
    core_qualifications: ["CFA_L2"],
    min_cleared_level: "CFA_L2",
  },
  {
    id: "j4",
    title: "Portfolio Analyst",
    company: "Kotak AMC",
    location: "Mumbai",
    job_type: "Full-time",
    description: "Fund research and portfolio construction support.",
    course_required: "CFA_L1,CFA_L2",
    exp_band_required: "1-3",
    skills_required: "Bloomberg, Excel, Research",
    posted_date: "2026-04-30T00:00:00Z",
    deadline: d1,
    status: "open",
    core_qualifications: ["CFA_L1"],
    min_cleared_level: "CFA_L1",
  },
  {
    id: "j5",
    title: "Financial Analyst Intern",
    company: "FinTree Education",
    location: "Pune",
    job_type: "Internship",
    description: "Support content team with curriculum research.",
    course_required: "CFA_L1",
    exp_band_required: "0-1",
    skills_required: "PowerPoint, Research",
    posted_date: "2026-05-06T00:00:00Z",
    deadline: d10,
    status: "open",
    core_qualifications: ["CFA_L1"],
    min_cleared_level: "CFA_L1",
  },
  {
    id: "j6",
    title: "Credit Analyst",
    company: "Bajaj Finance",
    location: "Pune",
    job_type: "Full-time",
    description: "Corporate credit underwriting and monitoring.",
    course_required: "CFA_L1,CFA_L2",
    exp_band_required: "1-3",
    skills_required: "Credit Analysis, Excel",
    posted_date: "2026-05-03T00:00:00Z",
    deadline: d5,
    status: "open",
    core_qualifications: ["CFA_L1"],
    min_cleared_level: "CFA_L1",
  },
]

/* ─── Community Chat ─── */

export const demoCommunityRooms = [
  {
    id: "room-1",
    slug: "cfa-l1-discussions",
    title: "CFA L1 Discussions",
    course: "CFA_L1",
    attempt: "2026_Jan",
    description: "General discussion forum for CFA Level I Jan 2026 batch.",
    member_count: 142,
    message_count: 1284,
    last_message_at: "2026-05-08T12:00:00Z",
    last_message_preview: "Has anyone finished FRA yet?",
    last_message_sender_name: "Priya",
    unread_count: 3,
    is_muted: false,
    created_at: "2026-01-15T00:00:00Z",
  },
  {
    id: "room-2",
    slug: "exam-strategy",
    title: "Exam Strategy & Tips",
    course: null,
    attempt: null,
    description: "Share strategies, tips, and motivation for upcoming exams.",
    member_count: 289,
    message_count: 3420,
    last_message_at: "2026-05-08T10:30:00Z",
    last_message_preview: "Remember: process of elimination is your friend.",
    last_message_sender_name: "Utkarsh Sir",
    unread_count: 1,
    is_muted: false,
    created_at: "2025-06-01T00:00:00Z",
  },
]

export const demoCommunityMessages = [
  { id: "msg-1", room_id: "room-1", sender_type: "student" as const, sender_id: "student-2", sender_name: "Priya", content: "Has anyone finished FRA yet?", file_url: null, file_name: null, file_size: null, file_type: null, reply_to: null, edited_at: null, created_at: "2026-05-08T12:00:00Z" },
  { id: "msg-2", room_id: "room-1", sender_type: "student" as const, sender_id: DEMO_PHONE, sender_name: "Shreyash", content: "Yes! Just completed Long-Lived Assets today.", file_url: null, file_name: null, file_size: null, file_type: null, reply_to: "msg-1", edited_at: null, created_at: "2026-05-08T12:05:00Z" },
  { id: "msg-3", room_id: "room-1", sender_type: "student" as const, sender_id: "student-3", sender_name: "Rohan", content: "How was the difficulty level?", file_url: null, file_name: null, file_size: null, file_type: null, reply_to: null, edited_at: null, created_at: "2026-05-08T12:08:00Z" },
  { id: "msg-4", room_id: "room-1", sender_type: "staff" as const, sender_id: "staff-1", sender_name: "Utkarsh Sir", content: "Great progress Shreyash! Keep the momentum going.", file_url: null, file_name: null, file_size: null, file_type: null, reply_to: null, edited_at: null, created_at: "2026-05-08T12:10:00Z" },
]

/* ─── Study Groups ─── */

export const demoStudyGroups = [
  {
    membershipId: "mg-1",
    role: "member",
    joinedAt: "2026-02-01T00:00:00Z",
    codeOfConductAcceptedAt: "2026-02-01T00:00:00Z",
    group: {
      id: "sg-1",
      name: "CFA L1 Jan 2026 — Pune Batch",
      course: "CFA_L1",
      type: "batch",
      max_members: 50,
      batch_label: "Jan 2026",
      description: "Study group for Pune-based CFA L1 Jan 2026 students.",
    },
  },
]

export const demoStudyGroupMessages = [
  { id: "sgm-1", group_id: "sg-1", phone: "9999999992", content: "Anyone up for a mock test this weekend?", created_at: "2026-05-08T09:00:00Z", deleted_at: null, pinned: false, report_count: 0, voice_url: null },
  { id: "sgm-2", group_id: "sg-1", phone: DEMO_PHONE, content: "Count me in! I need to practice FRA.", created_at: "2026-05-08T09:05:00Z", deleted_at: null, pinned: false, report_count: 0, voice_url: null },
  { id: "sgm-3", group_id: "sg-1", phone: "9999999993", content: "Let's do Saturday 2 PM at the library.", created_at: "2026-05-08T09:10:00Z", deleted_at: null, pinned: false, report_count: 0, voice_url: null },
]

/* ─── Notifications ─── */

export const demoNotifications = [
  { id: "notif-1", phone: DEMO_PHONE, title: "Live class reminder", body: "Equity Valuation live at 6:30 PM today.", is_read: false, created_at: "2026-05-08T14:00:00Z" },
  { id: "notif-2", phone: DEMO_PHONE, title: "Weekly test deadline", body: "Equity weekly test closes tomorrow.", is_read: false, created_at: "2026-05-08T10:00:00Z" },
  { id: "notif-3", phone: DEMO_PHONE, title: "New badge earned", body: "You earned the 'Mock Master' badge!", is_read: true, created_at: "2026-05-05T09:00:00Z" },
  { id: "notif-4", phone: DEMO_PHONE, title: "Study streak alert", body: "You are on a 12-day streak. Keep going!", is_read: true, created_at: "2026-05-04T08:00:00Z" },
]

/* ─── Spaced Repetition ─── */

export const demoSpacedRepetitionCards = [
  { id: "sr-1", phone: DEMO_PHONE, topic_code: "quant_1", question: "What is the future value of ₹10,000 at 8% for 5 years?", answer: "₹14,693", next_review_at: today, interval_days: 1, ease_factor: 2.5, created_at: "2026-04-01T00:00:00Z" },
  { id: "sr-2", phone: DEMO_PHONE, topic_code: "fra_2", question: "Name three profitability ratios.", answer: "ROE, ROA, Net Profit Margin", next_review_at: tomorrow, interval_days: 3, ease_factor: 2.3, created_at: "2026-04-05T00:00:00Z" },
  { id: "sr-3", phone: DEMO_PHONE, topic_code: "equity_1", question: "What is the difference between primary and secondary markets?", answer: "Primary = new issuance; Secondary = trading between investors", next_review_at: dayAfter, interval_days: 7, ease_factor: 2.1, created_at: "2026-04-10T00:00:00Z" },
]

/* ─── Student Notes ─── */

export const demoStudentNotes = [
  { id: "note-1", phone: DEMO_PHONE, topic_code: "quant_3", content: "Remember: Type I error = reject true null. Type II = fail to reject false null.", created_at: "2026-04-20T10:00:00Z" },
  { id: "note-2", phone: DEMO_PHONE, topic_code: "fra_3", content: "LIFO reserve = FIFO inventory - LIFO inventory. Important for conversion.", created_at: "2026-04-25T11:00:00Z" },
]

/* ─── Saved Watch Items ─── */

export const demoSavedWatchItems = [
  {
    savedAt: "2026-05-07T10:00:00Z",
    kind: "youtube" as const,
    contentId: "yt-1",
    title: "CFA L1 — Alternative Investments Live",
    thumbnailUrl: "https://img.youtube.com/vi/5x12p9Fc8C0/mqdefault.jpg",
    refId: "yt-1",
    youtubePlaylistId: "PL_alt_inv",
  },
  {
    savedAt: "2026-05-06T12:00:00Z",
    kind: "reel" as const,
    contentId: "r1",
    title: "Alternative Investments explained in 60 seconds",
    thumbnailUrl: "https://img.youtube.com/vi/5x12p9Fc8C0/mqdefault.jpg",
    refId: "r1",
  },
]

/* ─── For You Bundle ─── */

export const demoForYouBundle = {
  todaysPick: {
    kind: "youtube" as const,
    playlist: demoYouTubePlaylists[0],
  },
  trendingPlaylists: [demoYouTubePlaylists[1], demoYouTubePlaylists[2]],
  trendingReels: [demoReels[1], demoReels[2]],
  newPlaylists: [demoYouTubePlaylists[3]],
  newReels: [demoReels[0]],
  examPlaylists: demoYouTubePlaylists,
  examReels: demoReels,
}

/* ─── Student Doubts ─── */

export const demoStudentDoubts = [
  { id: "doubt-1", phone: DEMO_PHONE, subject: "Quant — Time Value of Money", question: "How do I calculate PV of an annuity due on the BA II Plus?", answer: "Switch to BGN mode (2nd, PMT, 2nd, ENTER), then use the PV key.", status: "answered", category: "Quant", priority: "medium", created_at: "2026-05-06T10:00:00Z" },
  { id: "doubt-2", phone: DEMO_PHONE, subject: "FRA — Inventory Valuation", question: "When is LIFO reserve used in IFRS?", answer: null, status: "open", category: "FRA", priority: "high", created_at: "2026-05-08T08:00:00Z" },
]


/* ─── Support Chat ─── */

export const demoSupportConversation = {
  id: "conv-1",
  phone: DEMO_PHONE,
  assigned_to: "Utkarsh",
  status: "open",
  last_message_at: "2026-05-08T16:00:00Z",
  unread_count: 1,
  created_at: "2026-05-01T09:00:00Z",
}

export const demoSupportMessages = [
  { id: "msg-1", conversation_id: "conv-1", sender_type: "system" as const, sender_id: "system", content: "Welcome to FinTree support! How can we help you today?", file_url: null, file_name: null, message_type: null, created_at: "2026-05-01T09:00:00Z", read_at: "2026-05-01T09:01:00Z" },
  { id: "msg-2", conversation_id: "conv-1", sender_type: "student" as const, sender_id: DEMO_PHONE, content: "Hi, I am having trouble understanding the Black-Scholes model. Can someone explain it?", file_url: null, file_name: null, message_type: null, created_at: "2026-05-06T14:00:00Z", read_at: "2026-05-06T14:05:00Z" },
  { id: "msg-3", conversation_id: "conv-1", sender_type: "staff" as const, sender_id: "staff-1", content: "Sure! Think of Black-Scholes as a recipe that estimates the fair price of a European option. The key inputs are: stock price, strike price, time to expiration, risk-free rate, and volatility. Watch the fixed income playlist for a visual walkthrough.", file_url: null, file_name: null, message_type: null, created_at: "2026-05-06T14:10:00Z", read_at: "2026-05-06T14:15:00Z" },
  { id: "msg-4", conversation_id: "conv-1", sender_type: "student" as const, sender_id: DEMO_PHONE, content: "Thanks! That helps. Also, when is the next live class for Equity Valuation?", file_url: null, file_name: null, message_type: null, created_at: "2026-05-08T15:30:00Z", read_at: "2026-05-08T15:35:00Z" },
  { id: "msg-5", conversation_id: "conv-1", sender_type: "staff" as const, sender_id: "staff-1", content: "The next Equity Valuation live class is today at 6:30 PM IST. Don't miss it — we will cover DDM and multi-stage models.", file_url: null, file_name: null, message_type: null, created_at: "2026-05-08T16:00:00Z", read_at: null },
]
